import React, { useEffect, useMemo, useRef, useState } from "react";
import { getComparison, type ComparisonResponse, formatCents } from "../../helpers/Compare";
import AppLayout from "../layouts/AppLayout";
import { Head } from "@inertiajs/react";
// Если у тебя есть общий лейаут/Head, подключи как на категориях:
// import AppLayout from "../layouts/AppLayout";
// import { Head } from "@inertiajs/react";

export default function ComparePage() {
    // читаем ids из query (?ids=104,203,301)
    const parseIdsFromURL = () => {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get("ids") || "";
        return raw
            .split(/[,\s]+/)
            .map((x) => Number(x))
            .filter((x) => Number.isFinite(x) && x > 0);
    };

    const [ids, setIds] = useState<number[]>(parseIdsFromURL());
    const [data, setData] = useState<ComparisonResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [onlyDiff, setOnlyDiff] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const refreshURL = (nextIds: number[]) => {
        const params = new URLSearchParams(window.location.search);
        if (nextIds.length > 0) params.set("ids", nextIds.join(","));
        else params.delete("ids");
        const next = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, "", next);
    };

    const fetchData = async (nextIds: number[]) => {
        if (nextIds.length < 2) {
            setData(null);
            return;
        }
        try {
            setLoading(true);
            setError(null);
            if (abortRef.current) abortRef.current.abort();
            abortRef.current = new AbortController();

            const res = await getComparison(nextIds, { signal: abortRef.current.signal });
            setData(res);
        } catch (e: any) {
            if (e?.name !== "AbortError") setError("Failed to load comparison");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refreshURL(ids);
        fetchData(ids);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ids.join(",")]);

    // фильтрация колонок "только различающиеся"
    const visibleColumns = useMemo(() => {
        if (!data) return [];
        if (!onlyDiff) return data.columns;

        const cols: typeof data.columns = [];
        for (const col of data.columns) {
            const values = data.products.map((p) => (data.matrix[p.id] ?? {})[col.attribute_id] ?? "");
            const uniq = new Set(values.map((v) => String(v || "")));
            if (uniq.size > 1) cols.push(col);
        }
        return cols;
    }, [data, onlyDiff]);

    const removeId = (id: number) => {
        const next = ids.filter((x) => x !== id);
        setIds(next);
    };

    const addId = (id: number) => {
        if (!id || ids.includes(id)) return;
        const next = [...ids, id];
        setIds(next);
    };

    // простая форма добавления товара по ID
    const [addInput, setAddInput] = useState<string>("");

    // helpers
    const emptyState = ids.length < 2;

    return (
        <AppLayout> {/* если используешь общий лейаут */}
            <Head>
                <title>{`Compare products - Compare Prices in Canada | PriceHunt.ca`}</title>

            </Head>
            <div className="w-full">
                <div className="container max-w-dasha mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

                    <div className="flex flex-wrap justify-between items-end gap-4">
                        <div className="space-y-1">
                            <h1 className="text-2xl font-semibold">Compare products</h1>
                            <div className="text-sm text-gray-600">
                                Select at least two products to see the comparison table.
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <label className="inline-flex items-center cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={onlyDiff ? true : false}
                                    onChange={(e) => setOnlyDiff(e.target.checked)}
                                />
                                <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:w-5 after:h-5 after:bg-white after:rounded-full after:transition-all peer-checked:after:translate-x-full"></div>
                                <span className="ml-2 text-sm">Show differences only</span>
                            </label>

                            <div className="flex items-center gap-2">
                                <input
                                    className="border px-2 py-1 rounded w-28"
                                    placeholder="Add ID…"
                                    value={addInput}
                                    onChange={(e) => setAddInput(e.target.value)}
                                />
                                <button
                                    className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
                                    onClick={() => {
                                        const id = Number(addInput);
                                        if (Number.isFinite(id) && id > 0) addId(id);
                                        setAddInput("");
                                    }}
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Карточки выбранных товаров */}
                    <div className="overflow-x-auto">
                        <div className="min-w-[640px]">
                            <div className="flex gap-4">
                                {ids.length > 0
                                    ? ids.map((id) => (
                                        <ProductHeaderCard
                                            key={id}
                                            id={id}
                                            product={data ? data.products.find((p) => p.id === id) : undefined}
                                            loading={loading}
                                            onRemove={() => removeId(id)}
                                        />
                                    ))
                                    : null}
                            </div>
                        </div>
                    </div>

                    {/* Состояния */}
                    {error ? (
                        <div className="p-3 rounded bg-red-50 text-red-700">{error}</div>
                    ) : null}

                    {emptyState ? (
                        <div className="p-6 rounded border text-gray-600">
                            Add at least two product IDs to compare.
                        </div>
                    ) : loading ? (
                        <div className="grid min-h-[160px] w-full place-items-center">
                            <svg className="w-12 h-12 animate-spin text-blue-300" viewBox="0 0 64 64" fill="none">
                                <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="5" className="opacity-25" />
                                <path d="M32 4a28 28 0 0 1 28 28" stroke="currentColor" strokeWidth="5" className="opacity-90" />
                            </svg>
                        </div>
                    ) : data && data.products.length > 0 ? (
                        <ComparisonTable data={data} visibleColumns={visibleColumns} />
                    ) : null}
                </div>
            </div>
        </AppLayout>
    );
}

function ProductHeaderCard({
    id,
    product,
    loading,
    onRemove,
}: {
    id: number;
    product?: ComparisonResponse["products"][number];
    loading: boolean;
    onRemove: () => void;
}) {
    return (
        <div className="w-[280px] p-3 border rounded-lg bg-white flex gap-3">
            <div className="w-20 h-20 bg-gray-100 rounded overflow-hidden flex items-center justify-center">
                {product && product.image
                    ? <img src={product.image} alt={product.name} className="object-contain w-full h-full" />
                    : <div className="text-xs text-gray-500">{loading ? "…" : "No image"}</div>}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-500 truncate">
                    {product && product.brand ? product.brand.name : "—"}
                </div>
                <div className="font-medium leading-snug line-clamp-2">
                    {product ? product.name : `#${id}`}
                </div>
                <div className="mt-1 text-sm">
                    {product ? formatCents(product.min_price) : "—"}{" "}
                    <span className="text-gray-500 text-xs">
                        {product ? (product.offers ? `• ${product.offers} offers` : "• 0 offers") : ""}
                    </span>
                </div>
                <div className="mt-2 flex gap-2">
                    <a
                        href={product ? `/products/${product.alias}` : "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 text-xs underline"
                    >
                        {product ? "Open page" : "—"}
                    </a>
                    <button
                        className="ml-auto text-xs text-red-600 underline"
                        onClick={onRemove}
                        title="Remove from comparison"
                    >
                        Remove
                    </button>
                </div>
            </div>
        </div>
    );
}

function ComparisonTable({
    data,
    visibleColumns,
}: {
    data: ComparisonResponse;
    visibleColumns: ComparisonResponse["columns"];
}) {
    // горизонтальный скролл, липкая первая колонка, липкая шапка
    return (
        <div className="relative overflow-x-auto">
            <table className="min-w-[720px] w-full">
                <thead className="sticky top-0 bg-white z-10 border-b">
                    <tr>
                        <th className="text-left p-3 w-64 sticky left-0 bg-white z-20 border-r">Specification</th>
                        {data.products.map((p) => (
                            <th key={p.id} className="text-left p-3 min-w-[220px]">
                                <div className="font-medium">{p.name}</div>
                                <div className="text-xs text-gray-500">{p.sku || "—"}</div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {/* Price row (сверху, как быстрый ориентир) */}
                    <tr className="border-b bg-gray-50/50">
                        <td className="p-3 sticky left-0 bg-gray-50/50 z-10 border-r font-medium">Best current price</td>
                        {data.products.map((p) => (
                            <td key={p.id} className="p-3">{formatCents(p.min_price)}</td>
                        ))}
                    </tr>

                    {visibleColumns.map((col) => (
                        <tr key={col.attribute_id} className="border-b">
                            <td className="p-3 sticky left-0 bg-white z-10 border-r">
                                <div className="font-medium">{col.attribute_name}</div>
                                <div className="text-xs text-gray-500">{col.coverage} / {data.products.length}</div>
                            </td>
                            {data.products.map((p) => {
                                const val = (data.matrix[p.id] ?? {})[col.attribute_id] ?? null;
                                return (
                                    <td key={`${p.id}-${col.attribute_id}`} className="p-3 align-top">
                                        {val ? <span>{val}</span> : <span className="text-gray-400">—</span>}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}